/**
 * Live relay configuration helpers + safety gates (task-11).
 *
 * The E2E control channel requires:
 * 1. a paired device token (relay auth),
 * 2. the daemon's X25519 public key so the phone can derive the shared channel
 *    key. Local loopback pairing returns it as daemon_public_key; the
 *    Worker-only pairing path does not carry it; use the relay-bootstrap
 *    pairing flow (task-14/15) or Settings/env as a manual fallback.
 * 3. a **secure context** for `crypto.subtle` (HTTPS or localhost). Plain HTTP
 *    on a LAN address cannot run the E2E crypto path; demo mode remains usable.
 *
 * No key material is logged or persisted by these helpers.
 */
import { base64UrlDecode } from '../id';

export const DAEMON_DEVICE_ID_PATTERN = /^dev_[A-Za-z0-9_-]{6,64}$/;

/** Normalize a daemon device id (`dev_<id>`); returns '' when invalid. */
export function normalizeDaemonDeviceId(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  return DAEMON_DEVICE_ID_PATTERN.test(trimmed) ? trimmed : '';
}

/** Normalize a base64url X25519 public key (32 raw bytes); returns '' when invalid. */
export function normalizeDaemonE2ePublicKey(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  if (!/^[A-Za-z0-9_-]{43}$/u.test(trimmed)) return '';
  try {
    return base64UrlDecode(trimmed).length === 32 ? trimmed : '';
  } catch {
    return '';
  }
}

export interface SecureCryptoSettings {
  ok: boolean;
  reason?: string;
}

/**
 * `crypto.subtle` is only exposed in a secure context (HTTPS / localhost).
 * Node (tests) has `crypto.subtle` and no `window.isSecureContext`, which is
 * treated as available so the pure logic is testable.
 */
export function secureCryptoStatus(): SecureCryptoSettings {
  const cryptoApi = (globalThis as unknown as { crypto?: { subtle?: unknown } }).crypto;
  if (!cryptoApi || !cryptoApi.subtle) {
    return { ok: false, reason: '当前环境缺少 WebCrypto（crypto.subtle）' };
  }
  const windowLike = (globalThis as unknown as { window?: { isSecureContext?: boolean } }).window;
  if (windowLike && windowLike.isSecureContext === false) {
    return {
      ok: false,
      reason:
        '需要 HTTPS 安全上下文（HTTP 局域网下 WebCrypto 不可用；可用 localhost 或 HTTPS 隧道）',
    };
  }
  return { ok: true };
}

export interface LiveRelayPlanInput {
  demoMode: boolean;
  paired: boolean;
  workerUrl: string;
  daemonDeviceId: string;
  daemonE2ePublicKey: string;
  secure?: SecureCryptoSettings;
}

export interface LiveRelayPlan {
  /** Relay should be connected and used for control/chat/exec. */
  enabled: boolean;
  /** Relay URL is derivable and daemon identity is configured. */
  configured: boolean;
  secure: boolean;
  /** User-facing reason when `enabled` is false (empty when enabled). */
  reason?: string;
}

/**
 * Decide whether live relay mode can be used. Returns a user-facing reason for
 * every unavailable case so the UI never pretends the E2E channel works.
 */
export function planLiveRelay(input: LiveRelayPlanInput): LiveRelayPlan {
  const secure = input.secure ?? secureCryptoStatus();
  const deviceId = normalizeDaemonDeviceId(input.daemonDeviceId);
  const publicKey = normalizeDaemonE2ePublicKey(input.daemonE2ePublicKey);
  const configured = Boolean(input.workerUrl.trim() && input.paired && deviceId && publicKey);
  if (input.demoMode) {
    return { enabled: false, configured, secure: secure.ok, reason: '演示模式（本地 mock）' };
  }
  if (!input.paired) {
    return {
      enabled: false,
      configured: false,
      secure: secure.ok,
      reason: '未配对（需要 device token）',
    };
  }
  if (!configured) {
    return {
      enabled: false,
      configured: false,
      secure: secure.ok,
      reason:
        '未配置 live relay：设置页填写 daemon device id（dev_<id>）与 X25519 公钥（base64url 32 字节）',
    };
  }
  if (!secure.ok) {
    return {
      enabled: false,
      configured: true,
      secure: false,
      reason: `live E2E 不可用：${secure.reason ?? '需要 HTTPS 安全上下文'}`,
    };
  }
  return { enabled: true, configured: true, secure: true };
}
